import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  getAuthSession,
  getRepositoryLineage,
  listRepositoryLifecycleRuns,
  synchronizeRepository,
} from '@/lib/api';
import type {
  RepositoryLineageResponse,
  RepositoryLifecycleRunResponse,
  RepositorySyncRequest,
  RepositorySyncResult,
} from '@/lib/api';
import {
  lifecycleError,
  refreshRepositoryLifecycle,
  repositoryLink,
  singleFlight,
  validRelease,
} from './repository-lifecycle-ui';

export function RepositoryLineagePanel({ projectId }: { projectId: string }): React.ReactElement {
  const lineage = useQuery({
    queryKey: ['repository-lineage', projectId],
    queryFn: () => getRepositoryLineage(projectId),
    staleTime: 30_000,
  });
  const runs = useQuery({
    queryKey: ['repository-lifecycle-runs', projectId],
    queryFn: () => listRepositoryLifecycleRuns(projectId),
    enabled: !!lineage.data,
    staleTime: 30_000,
  });
  return (
    <>
      <RepositoryLineageInventory
        lineage={lineage.data ?? null}
        loading={lineage.isPending}
        error={lineage.isError}
        runs={runs.data ?? []}
        runsLoading={!!lineage.data && runs.isPending}
        runsError={!!lineage.data && runs.isError}
        onRetry={() => {
          void lineage.refetch();
          if (lineage.data) void runs.refetch();
        }}
      />
      {lineage.data && (
        <RepositorySyncControls
          key={projectId}
          projectId={projectId}
          lineage={lineage.data}
          inventoryStale={lineage.isError}
        />
      )}
    </>
  );
}

const statusLabels: Record<RepositoryLineageResponse['sync_status'], string> = {
  unprovisioned: 'Not provisioned',
  provisioning: 'Provisioning',
  current: 'Up to date',
  update_available: 'Update available',
  syncing: 'Synchronizing',
  blocked: 'Synchronization blocked',
  failed: 'Synchronization failed',
  adoption_required: 'Migration blocked: product ancestry must be established',
};

export function RepositoryLineageInventory({
  lineage,
  runs,
  loading = false,
  error = false,
  runsLoading = false,
  runsError = false,
  onRetry,
}: {
  lineage: RepositoryLineageResponse | null;
  runs: RepositoryLifecycleRunResponse[];
  loading?: boolean;
  error?: boolean;
  runsLoading?: boolean;
  runsError?: boolean;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <section
      aria-label="Repository lineage"
      className="space-y-2 border-t border-border pt-3 text-xs text-text-secondary"
    >
      <h3 className="font-medium text-text-primary">Repository lineage</h3>
      {loading ? (
        <p role="status">Loading repository lineage…</p>
      ) : (
        <>
          {error && (
            <div role="alert">
              <p>
                Unable to refresh repository inventory.
                {lineage && ' Displayed inventory may be stale.'}
              </p>
              <Button size="sm" variant="outline" onClick={onRetry}>
                Retry
              </Button>
            </div>
          )}
          {!lineage ? (
            !error && (
              <p>
                Repository lineage unavailable. This project may be unmanaged or no longer
                accessible.
              </p>
            )
          ) : (
            <>
              <p className="font-medium">{statusLabels[lineage.sync_status]}</p>
              <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
                <dt>Product source</dt>
                <dd>
                  {lineage.product_source_id} · {lineage.product_source_version}
                </dd>
                <dt>Upstream repository</dt>
                <dd className="break-all">{lineage.upstream_repository_url}</dd>
                <dt>Customer code repository</dt>
                <dd className="break-all">
                  {lineage.downstream_repository_url ?? 'Not provisioned'}
                </dd>
                <dt>Configuration repository</dt>
                <dd className="break-all">{lineage.config_repository_url ?? 'Not provisioned'}</dd>
                <dt>Creation SHA</dt>
                <dd className="break-all font-mono">{lineage.creation_sha}</dd>
                <dt>Current release</dt>
                <dd>{lineage.applied_release_tag ?? 'Not recorded'}</dd>
                <dt>Applied SHA</dt>
                <dd className="break-all font-mono">
                  {lineage.applied_release_sha ?? 'Not recorded'}
                </dd>
                <dt>Pending release</dt>
                <dd>{lineage.pending_release_tag ?? 'None recorded'}</dd>
                <dt>Pending SHA</dt>
                <dd className="break-all font-mono">
                  {lineage.pending_release_sha ?? 'None recorded'}
                </dd>
                <dt>Configuration health</dt>
                <dd>
                  {lineage.configuration_health === 'unknown'
                    ? 'Not assessed'
                    : lineage.configuration_health}
                </dd>
                <dt>Bot access</dt>
                <dd>{lineage.bot_access === 'unknown' ? 'Not assessed' : lineage.bot_access}</dd>
                <dt>Divergence</dt>
                <dd>
                  {lineage.divergence
                    ? `${String(lineage.divergence.ahead)} ahead · ${String(lineage.divergence.behind)} behind`
                    : 'Not assessed'}
                </dd>
                <dt>Extension paths</dt>
                <dd className="break-all">
                  {lineage.extension_paths.join(', ') || 'None declared'}
                </dd>
                <dt>Extension changes</dt>
                <dd className="break-all">
                  {lineage.extension_changes?.join(', ') ?? 'Not assessed'}
                  {lineage.extension_changes?.length === 0 && 'None'}
                </dd>
                <dt>Product-owned customization debt</dt>
                <dd className="break-all">
                  {lineage.product_owned_changes?.join(', ') ?? 'Not assessed'}
                  {lineage.product_owned_changes?.length === 0 && 'None'}
                </dd>
                <dt>Inventory updated</dt>
                <dd>{lineage.updated_at}</dd>
              </dl>
              {lineage.pull_request && (
                <a
                  className="underline"
                  href={lineage.pull_request.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Update PR #{lineage.pull_request.number}
                </a>
              )}
              <h4 className="font-medium">Recent lifecycle runs</h4>
              <p>
                Failed operations require reconciliation and retry of the unchanged request. Blocked
                attempts require a fresh preview and new approval.
              </p>
              {runsError ? (
                <div role="alert">
                  <p>
                    Unable to load lifecycle history.
                    {runs.length > 0 && ' Displayed history may be stale.'}
                  </p>
                  <Button size="sm" variant="outline" onClick={onRetry}>
                    Retry
                  </Button>
                </div>
              ) : runsLoading ? (
                <p role="status">Loading lifecycle history…</p>
              ) : (
                runs.length === 0 && <p>No lifecycle runs recorded.</p>
              )}
              {runs.length > 0 && (
                <ul className="space-y-1">
                  {runs.map(run => (
                    <li key={run.id} className="break-all">
                      {run.operation} · {run.mode} · {run.current_status} ·{' '}
                      {run.target_release_tag ?? 'No release'}
                      {run.target_release_sha && (
                        <code className="block">{run.target_release_sha}</code>
                      )}
                      <span className="block text-text-tertiary">
                        {run.id} · {run.created_at}
                      </span>
                      {run.pull_request && (
                        <a
                          className="underline"
                          href={run.pull_request.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Update PR #{run.pull_request.number}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}

export function buildSyncPreparation(
  releaseRef: string,
  releaseSha: string,
  preview: RepositorySyncResult | null,
  approved: boolean,
  idempotencyKey: string
): RepositorySyncRequest | null {
  if (
    !approved ||
    !validRelease(releaseRef, releaseSha) ||
    preview?.status !== 'previewed' ||
    preview.upstreamSha !== releaseSha ||
    !preview.downstreamSha
  )
    return null;
  return {
    mode: 'prepare_pr',
    releaseRef,
    releaseSha,
    expectedHeadSha: preview.downstreamSha,
    idempotencyKey,
    approvalConfirmed: true,
  };
}

export function RepositorySyncReceipt({
  result,
}: {
  result: RepositorySyncResult;
}): React.ReactElement {
  return (
    <section aria-label="Release sync receipt" className="space-y-2 break-all" role="status">
      <p>
        {result.replayed ? 'Replayed' : 'Recorded'} result: {result.status} · Run {result.runId}
      </p>
      {result.reason && (
        <p>Blocked: {result.reason}. Refresh inventory and preview a newly reviewed request.</p>
      )}
      {result.reason === 'adoption_required' && (
        <p>
          Migration blocked. An operator must prove ancestry, preserve backup and legacy refs, and
          separately approve the exact candidate before any default-branch change.
        </p>
      )}
      <dl>
        <dt>Product SHA</dt>
        <dd>{result.upstreamSha}</dd>
        <dt>Customer head</dt>
        <dd>{result.downstreamSha ?? 'Not established'}</dd>
        <dt>Rollback commit</dt>
        <dd>{result.rollbackRef ?? 'Not established'}</dd>
        <dt>Divergence</dt>
        <dd>
          {result.divergence.ahead} ahead · {result.divergence.behind} behind
        </dd>
        <dt>Changed paths</dt>
        <dd>{result.changedPaths.join(', ') || 'None'}</dd>
        <dt>Extension changes</dt>
        <dd>{result.extensionChanges.join(', ') || 'None'}</dd>
        <dt>Product-owned customization debt</dt>
        <dd>{result.productOwnedChanges.join(', ') || 'None'}</dd>
        <dt>Conflicts</dt>
        <dd>{result.conflicts.join(', ') || 'None'}</dd>
      </dl>
      <p>
        Gates:{' '}
        {result.gates.length
          ? result.gates.map(gate => `${gate.id}: ${gate.passed ? 'passed' : 'failed'}`).join(', ')
          : 'Not run during preview'}
      </p>
      {result.pullRequest && repositoryLink(result.pullRequest.url) && (
        <a className="underline" href={result.pullRequest.url} target="_blank" rel="noreferrer">
          Update PR #{result.pullRequest.number}
        </a>
      )}
      {result.adoption && (
        <p>
          Migration receipt: {result.adoption.status} · Candidate{' '}
          {result.adoption.candidateSha ?? 'Not established'} · Preserved refs:{' '}
          {result.adoption.backupRefs.join(', ')}
        </p>
      )}
    </section>
  );
}

function RepositorySyncControls({
  projectId,
  lineage,
  inventoryStale,
}: {
  projectId: string;
  lineage: RepositoryLineageResponse;
  inventoryStale: boolean;
}): React.ReactElement {
  const client = useQueryClient();
  const { data: session } = useQuery({ queryKey: ['authSession'], queryFn: getAuthSession });
  const canPrepare = session?.authenticated === true && session.operator?.role === 'admin';
  const [releaseRef, setReleaseRef] = useState(
    lineage.pending_release_tag
      ? lineage.pending_release_tag.startsWith('refs/tags/')
        ? lineage.pending_release_tag
        : `refs/tags/${lineage.pending_release_tag}`
      : 'refs/tags/'
  );
  const [releaseSha, setReleaseSha] = useState(lineage.pending_release_sha ?? '');
  const [approved, setApproved] = useState(false);
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<RepositorySyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const request = useRef<RepositorySyncRequest | null>(null);
  const submit = useRef(singleFlight());
  const reset = (): void => {
    setApproved(false);
    setResult(null);
    setError(null);
    request.current = null;
  };
  const execute = (input: RepositorySyncRequest, retry = false): void => {
    void submit.current(async () => {
      if (retry ? input !== request.current : inventoryStale) return;
      if (
        input.mode === 'prepare_pr' &&
        (!canPrepare || (retry ? input.approvalConfirmed !== true : !approved))
      )
        return;
      request.current = input;
      setPending(true);
      setError(null);
      try {
        setResult(await synchronizeRepository(projectId, input));
        setApproved(false);
      } catch (failure) {
        setError(lifecycleError(failure));
      } finally {
        await refreshRepositoryLifecycle(client);
        setPending(false);
      }
    });
  };
  const preparation = buildSyncPreparation(releaseRef, releaseSha, result, approved, 'validation');
  return (
    <section aria-label="Product release update" className="space-y-2 border-t pt-3 text-xs">
      <h3 className="font-medium">Product release update</h3>
      <p>
        Preview records an audit run and inspects the exact release without changing remote
        repositories. Preparing creates a branch and reviewable update PR; it never merges, deletes
        a repository, deploys, delivers, or activates.
      </p>
      {inventoryStale && (
        <p role="alert">
          Inventory is stale. Refresh repository inventory before editing or starting a new preview
          or preparation. Retain the unchanged request for reconciliation and retry.
        </p>
      )}
      <fieldset disabled={pending || inventoryStale} className="space-y-2">
        <label className="block">
          Approved release ref
          <Input
            value={releaseRef}
            onChange={event => {
              setReleaseRef(event.target.value);
              reset();
            }}
          />
        </label>
        <label className="block">
          Exact release SHA
          <Input
            value={releaseSha}
            onChange={event => {
              setReleaseSha(event.target.value);
              reset();
            }}
          />
        </label>
        <Button
          size="sm"
          variant="outline"
          disabled={!validRelease(releaseRef, releaseSha)}
          onClick={() => {
            setApproved(false);
            execute({
              mode: 'preview',
              releaseRef,
              releaseSha,
              idempotencyKey: crypto.randomUUID(),
            });
          }}
        >
          Preview release
        </Button>
        {result?.status === 'previewed' && (
          <>
            <p>
              Approval binds release {releaseRef} at {releaseSha} to customer head{' '}
              {result.downstreamSha}.
            </p>
            <label className="flex gap-2">
              <input
                type="checkbox"
                checked={approved}
                disabled={!canPrepare || !!error}
                onChange={event => {
                  setApproved(event.target.checked);
                }}
              />
              I approve creating an update branch and PR for these exact SHAs.
            </label>
          </>
        )}
        {!canPrepare && <p>An organization administrator must approve update preparation.</p>}
        <Button
          size="sm"
          disabled={!canPrepare || !preparation || !!error}
          onClick={() => {
            const input = buildSyncPreparation(
              releaseRef,
              releaseSha,
              result,
              approved,
              crypto.randomUUID()
            );
            if (input) execute(input);
          }}
        >
          Prepare update PR
        </Button>
      </fieldset>
      {pending && <p role="status">Repository operation running…</p>}
      {request.current && <p>Request key: {request.current.idempotencyKey}</p>}
      {(error || (inventoryStale && request.current)) && (
        <div role="alert">
          <p>{error}</p>
          <Button
            size="sm"
            disabled={
              pending ||
              (request.current?.mode === 'prepare_pr' &&
                (!canPrepare || request.current.approvalConfirmed !== true))
            }
            onClick={() => {
              if (request.current) execute(request.current, true);
            }}
          >
            Retry unchanged request
          </Button>
        </div>
      )}
      {result && <RepositorySyncReceipt result={result} />}
    </section>
  );
}
